/**
 * Room dimension graphics aligned with v3 `ui-view-tables` `_DrawArea`:
 * vertical cota on the **screen-right** edge of the given box, horizontal on the **screen-bottom**,
 * metres with two decimals. Callers pass extents in cm for each axis (swap when the room is shown at 90°).
 */
import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';

/** Type definition for Konva group used for room dimension rendering. */
export type FloorPlanRoomDimensionKonvaGroup = {
  add: (n: unknown) => void;
  id: (id?: string) => string | void;
  setAttrs: (attrs: Record<string, unknown>) => void;
};

/** Konva constructors used by {@link createUppFloorPlanRoomDimensionGroup}. */
export interface FloorPlanRoomDimensionsKonva {
  readonly Group: new (config?: Record<string, unknown>) => {
    add: (n: unknown) => void;
    id: (id?: string) => string | void;
    setAttrs: (attrs: Record<string, unknown>) => void;
  };
  readonly Line: new (config?: Record<string, unknown>) => unknown;
  readonly Text: new (config?: Record<string, unknown>) => {
    width: () => number;
    height: () => number;
    offsetX: (v: number) => void;
    offsetY: (v: number) => void;
    rotation: (deg: number) => void;
  };
}

const TRI_HALF = 5;
const TRI_SIDE = 10;
const TEXT_MARGIN = 10;

/**
 * Horizontal (right) and vertical (bottom) overflow of dimension graphics vs the room `Rect`,
 * in the same px space as {@link createUppFloorPlanRoomDimensionGroup}. Use with
 * {@link computeUppFloorPlanViewTransform} `dimensionInset*` so labels stay inside the host.
 *
 * The right-edge label is rotated **-90°** around its own center, so its footprint swaps axes:
 * the label string's rendered *width* becomes vertical span (irrelevant to `right`), while its
 * *height* (~font line height) becomes the horizontal overflow past the room's right edge — the
 * same overflow the (unrotated) bottom label gets from its own height. Sizing `right` off the
 * label's text *width* would reserve far more horizontal gutter than the rotated glyphs ever use,
 * shrinking the fitted room (and inflating every margin, since scale is uniform) for no visual gain.
 */
export function estimateRoomDimensionLabelOverflowPx(fontSize: number): {
  right: number;
  bottom: number;
} {
  const fs = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 12;
  const right = Math.ceil(TEXT_MARGIN + fs + TRI_HALF + 6);
  const bottom = Math.ceil(TEXT_MARGIN + fs + 8);
  return { right, bottom };
}

/**
 * Builds a non-interactive group with dimension shafts, arrow heads, and labels (cm → `"X.XX m"`).
 * @param extentHorizontalCm — span along the **bottom** dimension line (screen X).
 * @param extentVerticalCm — span along the **right** dimension line (screen Y).
 */
export function createUppFloorPlanRoomDimensionGroup(
  K: FloorPlanRoomDimensionsKonva,
  theme: UppFloorPlanTheme,
  roomWidthPx: number,
  roomHeightPx: number,
  extentHorizontalCm: number,
  extentVerticalCm: number,
): FloorPlanRoomDimensionKonvaGroup {
  const W = roomWidthPx;
  const H = roomHeightPx;
  const color = theme.roomDimensionColor;
  const fs = theme.roomDimensionFontSize;
  const t1 = TRI_HALF;
  const t2 = TRI_SIDE;
  const tm = TEXT_MARGIN;

  const g = new K.Group({ listening: false, id: 'upp-floor-plan-room-dimensions' });

  // Right vertical dimension (room height in m)
  g.add(
    new K.Line({
      points: [W, 0, W, H],
      stroke: color,
      strokeWidth: 1,
      listening: false,
    }),
  );
  g.add(
    new K.Line({
      points: [W, 0, W - t1, t2, W + t1, t2],
      closed: true,
      fill: color,
      stroke: color,
      strokeWidth: 1,
      listening: false,
    }),
  );
  g.add(
    new K.Line({
      points: [W, H, W - t1, H - t2, W + t1, H - t2],
      closed: true,
      fill: color,
      stroke: color,
      strokeWidth: 1,
      listening: false,
    }),
  );

  const hLabel = `${(extentVerticalCm / 100).toFixed(2)} m`;
  const vertText = new K.Text({
    x: W + tm,
    y: H / 2,
    text: hLabel,
    fontSize: fs,
    fontFamily: 'sans-serif',
    fill: color,
    listening: false,
  });
  vertText.offsetX(vertText.width() / 2);
  vertText.offsetY(vertText.height() / 2);
  vertText.rotation(-90);
  g.add(vertText);

  // Bottom horizontal dimension (room width in m)
  g.add(
    new K.Line({
      points: [0, H, W, H],
      stroke: color,
      strokeWidth: 1,
      listening: false,
    }),
  );
  g.add(
    new K.Line({
      points: [0, H, t2, H - t1, t2, H + t1],
      closed: true,
      fill: color,
      stroke: color,
      strokeWidth: 1,
      listening: false,
    }),
  );
  g.add(
    new K.Line({
      points: [W, H, W - t2, H - t1, W - t2, H + t1],
      closed: true,
      fill: color,
      stroke: color,
      strokeWidth: 1,
      listening: false,
    }),
  );

  const wLabel = `${(extentHorizontalCm / 100).toFixed(2)} m`;
  const horizText = new K.Text({
    x: W / 2,
    y: H + tm,
    text: wLabel,
    fontSize: fs,
    fontFamily: 'sans-serif',
    fill: color,
    listening: false,
  });
  horizText.offsetX(horizText.width() / 2);
  horizText.offsetY(horizText.height() / 2);
  g.add(horizText);

  return g;
}
